import Vue from 'vue';
import Vuex from 'vuex';
import {VueRouter} from "vue-router/types/router";
import {ProgramPaths} from "@/enum/ProgramPaths";
import {PersonelEntity} from "@/entity/PersonelEntity";
import gorevYoneticisi from "@/plugins/uyap-plugin/store/modules/GorevYoneticisi";
import takipDetay from "@/store/modules/takipDetay";
import takipEkle from "@/store/modules/takipEkle";
import theme from "@/store/modules/theme";
import app from "@/main";
import {AppState} from "@/types";
import VueSocketIOExt from 'vue-socket.io-extended';
import {io} from 'socket.io-client';
import takipHazirla from '@/store/modules/formWizards/takipHazirla';
import mtsTakipHazirla from '@/store/modules/formWizards/mtsTakipHazirla';
import sorguOneri from '@/store/modules/sorguOneri';
import senkronPlan from '@/store/modules/senkronPlan';
import senkronAyarlari from '@/store/modules/senkronAyarlari';
import imzaKimligi from '@/store/modules/imzaKimligi';
import uyapEngeli from '@/store/modules/uyapEngeli';
import {KullaniciBilgileriCevap} from "@/plugins/uyap-plugin/uyap/KullaniciBilgileri";
import {sortMahkemelerByIsimAsc} from "@/utils/mahkemeSort";
import {offerFromLisansDurum} from "@/utils/renewalOffer";
import {demoStatusFromLisansDurum} from "@/utils/demoStatus";
import {tazelemeGecikmesi} from "@/utils/lisansKisit";
import {benIstegiBaslat, benYanitiYazilsinMi} from "@/utils/benIstegi";
import _swal from "sweetalert2";
import {
    senkronAralikIcindeMi,
    senkronKapisiTazeMi,
    senkronKapiZamaniOku,
    senkronKapiZamaniYaz
} from "@/utils/senkronKapisi";

// İmza websocket
const socket = io('https://imza.eicrapro.com:3000', {
    autoConnect: true,
    reconnection: true
});
Vue.use(VueSocketIOExt, socket);
Vue.use(Vuex);
// DEV-2113 — /ben kural özetinin (ozellik_durumu) tazelenmesi. Sekme başına tek
// zamanlayıcı, aynı anda tek istek (bkz. lisansKisit.ts -> tazelemeGecikmesi).
let ozellikTazelemeZamanlayici: ReturnType<typeof setTimeout> | null = null;
let ozellikTazelemeIstegi: Promise<any> | null = null;
let sonOzellikTazelemesi = 0;
// `ozellikDurumunuTazele`nin son BAŞARILI yanıtının (epoch ms) geldiği an. `sonOzellikTazelemesi`
// istek BAŞLARKEN damgalanır (başarısız denemeyi de sayar); kapı tazelemesi (DEV-2603)
// başarısız bir özellik denemesi yüzünden aralık boyunca engellenmesin diye bunu kullanır.
let sonOzellikBasarisi = 0;

export const store = new Vuex.Store({
    modules: {
        // @ts-ignore
        gorevYoneticisi,
        takipDetay,
        takipEkle,
        theme,
        takipHazirla,
        mtsTakipHazirla,
        sorguOneri,
        senkronPlan,
        senkronAyarlari,
        imzaKimligi,
        uyapEngeli
    },
    state: <AppState>{
        accessToken: localStorage.getItem('accessToken'),
        tenant_id: sessionStorage.getItem('tenantId'),
        user: <PersonelEntity>JSON.parse(sessionStorage.getItem('user') ?? "{}"),
        activeProcesses: JSON.parse(sessionStorage.getItem("activeProcesses") ?? "[]"),
        createTcKimlikErrors: [],
        icraProTakipBilgileri : {},
        xml: null,
        eicraproDevMode: false,  // Mock data kullanmak için true yap
        uyapConnection: false,
        ssoConfig: null,
        uyapAvukatBilgileri: undefined,
        uyapSorguBakiye: undefined,     // Sorgu bakiyesi
        uyapAvukatBakiye: undefined,    // E-Barobirlik bakiyesi
        avukatlar: [],
        mahkemeler:[],
        noterler:[],
        tenantList: [],
        kurlar: [],
        personeller: [],
        tenantListPromise: null,
        // `senkronDurumunuTazele` için SÜREN istek (eşzamanlı çağrılar tek `/ben` paylaşır;
        // bitince null'lanır). `tenantListPromise` ile aynı desen: promise Vue tarafından
        // gözlenmez. Sıklık sınırı `sessionStorage`daki zaman damgası ve aşağıdaki son
        // deneme zamanıyla sağlanır.
        senkronTazelemeIstegi: null,
        // Kapı için `/ben`in en son DENENDİĞİ an (epoch ms; başarılı ya da başarısız).
        // Bellekte durur: kalıcı olan, yalnız başarıyla alınan değerin damgasıdır.
        senkronSonDenemeZamani: 0,
        ssoUri: process.env.VUE_APP_SSO_TARGET,
        programs: [
            {
                name: "Ofispro",
                programPath: ProgramPaths.ofispro,
                urls: [
                    "ofispro.com",
                    "test.ofispro.com",
                    "ofispro.lcl",
                    "localhost",
                ],
            },
            {
                name: "IcraproWeb",
                programPath: ProgramPaths.icrapro,
                urls: [
                    "icrapro.com",
                    "test.icrapro.com",
                    "cloudbeta.zirve-bilgisayar.com",
                    "cloudopt.zirve-bilgisayar.com",
                    "cloudtest.zirve-bilgisayar.com",
                    "cloudtest1.zirve-bilgisayar.com",
                    "cloudtest2.zirve-bilgisayar.com",
                    "cloudtest3.zirve-bilgisayar.com",
                    "cloudtest4.zirve-bilgisayar.com",
                    "cloudicra.neova.com.tr",
                    "okandemirkan.icrapro.com",
                    "cloud.zirve-bilgisayar.com",
                    "icrapro.lcl",
                ]
            },
            {
                name: "e-İcrapro",
                programPath: ProgramPaths.eicrapro,
                urls: [
                    "eicrapro.com",
                    "test.eicrapro.com",
                    "eicrapro.lcl",
                ]
            }
        ]
    },
    actions: {
        // Yenileme teklifi ve demo durumu için AYRI İSTEK YOKTUR: ikisinin de ham
        // verisi /api/v1/ben yanıtındaki `lisans_durum` alanıyla geliyor ve
        // sessionStorage'daki user ile birlikte saklanıyor. Kademe/gün hesabı
        // renewalOfferFor / demoStatusFor getter'larında yapılır.
        // (Eski uçlar /v1/renewal-offer ve /v1/demo-status geriye dönük uyumluluk
        //  için API'de duruyor; bu uygulama artık çağırmıyor.)

        // Tek kullanımlık otomatik giriş + sepet URL'i alıp mağazaya yönlendirir.
        // product: 'icrapro' | 'eicrapro' — kullanıcının bulunduğu ürüne göre.
        async startRenewalCheckout(_ctx, product: 'icrapro' | 'eicrapro' = 'icrapro') {
            try {
                const result = await Vue.prototype.$http.post('/api/v1/renewal/checkout-link', {product});
                if (result?.url) {
                    window.location.href = result.url;
                } else {
                    app.$toast.error('Yenileme bağlantısı oluşturulamadı.');
                }
            } catch (e) {
                app.$toast.error('Yenileme bağlantısı oluşturulamadı. Lütfen tekrar deneyin.');
            }
        },
        // "Hemen Al": tek kullanımlık otomatik giriş + mağaza fiyatlandırma sayfası
        // URL'i alıp yönlendirir. product: 'icrapro' | 'eicrapro'.
        async startDemoPurchase(_ctx, product: 'icrapro' | 'eicrapro' = 'icrapro') {
            try {
                const result = await Vue.prototype.$http.post('/api/v1/demo/purchase-link', {product});
                if (result?.url) {
                    window.location.href = result.url;
                } else {
                    app.$toast.error('Satın alma bağlantısı oluşturulamadı.');
                }
            } catch (e) {
                app.$toast.error('Satın alma bağlantısı oluşturulamadı. Lütfen tekrar deneyin.');
            }
        },
        async fetchIcraProTakipBilgileri({ commit, state }, id) {
            try {
                const response = await Vue.prototype.$http.get(`/api/v1/takip/${id}`);
                commit('setIcraProTakipBilgileri', response.data);
            } catch (error) {
                console.error('Takip bilgileri alınırken hata oluştu:', error);
            }

        },
        async getKurlarMerkezBankasi({ state, commit }) {
            try {
                if (state.kurlar.length > 0)
                    return state.kurlar;
                let response = await Vue.prototype.$http.get("https://www.icrapro.com/todaykur.php");
                commit('setKurlar', response);
            } catch (e) {
                throw e;
            }
        },
        async getTenantList({ state, commit }) {
            try {
                console.log("getTenantList TRIGGERED");

                // 1) Eğer data zaten varsa direkt dön
                if (state.tenantList.length > 0) {
                    return state.tenantList;
                }

                // 2) Eğer istek devam ediyorsa onu beklet
                if (state.tenantListPromise) {
                    return await state.tenantListPromise;
                }

                // 3) Yeni bir promise yarat
                const promise = Vue.prototype.$http
                    .get("/api/v1/oauth/user-tenant-list")
                    .then(res => {
                        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
                        commit('setTenantList', list);
                        return list;
                    })
                    .finally(() => {
                        commit('setTenantListPromise', null);
                    });

                // Promise’i state’e kaydet
                commit('setTenantListPromise', promise);

                // İlk çağıran bunu bekler
                return await promise;

            } catch (e) {
                commit('setTenantListPromise', null);
                throw e;
            }
        },
        async getSelectedTenantId({state, commit}) {
            return sessionStorage.getItem("tenantId");
        },
        /**
         * DEV-2113 — /ben'i yeniden çeker; kural özeti tazelenir ve bir sonraki geçiş
         * anı için zamanlayıcı yeniden kurulur. Aynı anda tek istek gider.
         * `enAzAralikMs` verilirse son tazelemeden o kadar geçmeden istek atılmaz
         * (lisans 403'ü, bayat özet): tarayıcı saati kaymışsa ekran durmadan istemesin.
         */
        async ozellikDurumunuTazele({state, commit, dispatch}, secenek?: {enAzAralikMs?: number}) {
            if (secenek?.enAzAralikMs && Date.now() - sonOzellikTazelemesi < secenek.enAzAralikMs) {
                return state.user;
            }
            if (!ozellikTazelemeIstegi) {
                sonOzellikTazelemesi = Date.now();
                const no = benIstegiBaslat();
                ozellikTazelemeIstegi = app.$http.get("/api/v1/ben")
                    .then((user: any) => {
                        // DEV-2684: sonra başlamış bir /ben (benTazele) zaten yazdıysa bu yanıt daha eskidir; çağırana güncel kullanıcı döner.
                        if (!benYanitiYazilsinMi(no)) {
                            sonOzellikBasarisi = Date.now();
                            dispatch('ozellikTazelemesiniKur');
                            return state.user;
                        }
                        commit('setUser', user);
                        sonOzellikBasarisi = Date.now();
                        dispatch('ozellikTazelemesiniKur');
                        return user;
                    })
                    .finally(() => {
                        ozellikTazelemeIstegi = null;
                    });
            }
            return ozellikTazelemeIstegi;
        },
        /** Kural özetinin bayatlayacağı anda /ben'i yeniden çekecek zamanlayıcıyı kurar (öncekini siler). */
        ozellikTazelemesiniKur({state, dispatch}) {
            if (ozellikTazelemeZamanlayici) clearTimeout(ozellikTazelemeZamanlayici);
            ozellikTazelemeZamanlayici = null;
            // Date.now üzerinden: saat testte sabitlenebilsin (jest 24'te Date sahtelenmez).
            const gecikme = tazelemeGecikmesi(state.user?.ozellik_durumu, new Date(Date.now()));
            if (gecikme !== null) {
                ozellikTazelemeZamanlayici = setTimeout(() => {
                    ozellikTazelemeZamanlayici = null;
                    dispatch('ozellikDurumunuTazele').catch(() => undefined);
                }, gecikme);
            }
        },
        async getUser({state, commit}) {
            if (!state.user.id) {
                let response = await Vue.prototype.$http.get("/api/v1/ben");
                commit('setUser', response.data);
            }
            return state.user;
        },
        /**
         * Otomatik sorgu kapısını (`/ben` yanıtındaki `senkron_aktif`) sunucudan tazeler.
         *
         * Neden gerekli: kullanıcı `sessionStorage`da durur ve sayfa yenilemesinde
         * yeniden istenmez. Kapı sunucuda kapalıyken kaydedilmiş bir oturum, kapı
         * YENİDEN AÇILDIĞINDA bayat "kapalı" değerini taşırdı (açık değer de kapı
         * yeniden kapanınca bayatlar). Yalnız bu alan işlenir; kullanıcının geri
         * kalanına dokunulmaz.
         *
         * `/ben` ağırdır ve sayfa yenilemesi normalde sıfır istek eder; bu yüzden çağıran
         * `enAzAralikMs` verirse değer o süreden TAZEYSE (ya da o süre içinde bir deneme
         * yapıldıysa, başarısız olsa da) istek atılmaz. Taze sayılması için alanın gerçek
         * bir boolean olması gerekir: alan hiç gelmemiş (deploy öncesi oturum) değer her
         * zaman bir kez tazelenir. Süre verilmezse zorla istenir. Sunucudan alınan her
         * taze `/ben` yanıtı (`setUser`) da damgayı yazar; böylece girişten hemen sonra
         * ikinci bir istek gitmez. Süren istek varsa her durumda onu paylaşır.
         *
         * DEV-2113'ün `ozellikDurumunuTazele` eylemi de AYNI `/ben` yanıtını çeker (ve
         * `setUser` ile kapıyı da günceller). Çift istek koruması TEK YÖNLÜDÜR ve yalnız
         * o eylemin isteği SÜRERKEN geçerlidir: o zaman ayrı istek atılmaz, onun bitmesi
         * beklenir. Ek olarak son BAŞARILI özellik tazelemesi aralık içindeyse bu eylem atlanır;
         * başarısız bir özellik denemesi kapıyı engellemez (kapı kendi isteğini atar).
         * Tersi yönde koruma yoktur: `ozellikDurumunuTazele` süren kapı isteğine bakmaz
         * (kapının isteği `ozellik_durumu` ve lisans alanlarını işlemez, o yüzden ikinci istek gerekir).
         *
         * Sessiz çağrılır (`sessiz: true`: hata penceresi yok) ve ASLA fırlatmaz: hata
         * halinde değer değişmez (fail-closed: alan yoksa zaten kapalı). Yanıtta alan
         * yoksa (eski backend) kapalı yazılır.
         */
        async senkronDurumunuTazele({state, commit}, secenek?: {enAzAralikMs?: number}): Promise<void> {
            if (!state.user || !state.user.id) return;

            let istek = state.senkronTazelemeIstegi;
            if (!istek && ozellikTazelemeIstegi) {
                // `ozellikDurumunuTazele`nin `/ben`i sürüyor: aynı yanıt kapıyı da taşır ve
                // `setUser` kapıyı günceller; ikinci bir istek atılmaz. Hatası kapıyı değiştirmez.
                try {
                    await ozellikTazelemeIstegi;
                } catch (e) {
                    // Değer değişmez (fail-closed).
                }
                return;
            }
            if (!istek) {
                const aralik = secenek && secenek.enAzAralikMs;
                if (aralik) {
                    const simdi = Date.now();
                    // Son deneme (bizim; başarısız olsa da) ya da `ozellikDurumunuTazele`nin son
                    // BAŞARILI yanıtı aralık içindeyse ya da eldeki değer taze ise atla.
                    const sonDeneme = Math.max(state.senkronSonDenemeZamani, sonOzellikBasarisi);
                    if (senkronAralikIcindeMi(sonDeneme, simdi, aralik)
                        || senkronKapisiTazeMi((state.user as any).senkron_aktif, senkronKapiZamaniOku(), simdi, aralik)) {
                        return;
                    }
                }

                commit('setSenkronSonDenemeZamani', Date.now());
                istek = (async (): Promise<void> => {
                    try {
                        const kullanici = await Vue.prototype.$http.get("/api/v1/ben", {sessiz: true});
                        commit('setSenkronAktif', !!kullanici && kullanici.senkron_aktif === true);
                        senkronKapiZamaniYaz();
                    } catch (e) {
                        // Değer değişmez; sıklık sınırı deneme zamanıyla korunur.
                    }
                })();
                commit('setSenkronTazelemeIstegi', istek);
                const bu = istek;
                bu.then(() => {
                    if (state.senkronTazelemeIstegi === bu) commit('setSenkronTazelemeIstegi', null);
                });
            }

            await istek;
        },
        async getAvukatlar({state, commit}) {
            if (state.avukatlar.length == 0) {
                let response = await app.$http.get("/api/v1/personel/filter?type=avukat");
                commit('setAvukatlar', response.data);
            }
            return state.avukatlar;
        },
        async getMahkemeler({state, commit}) {
            if (state.mahkemeler.length == 0) {
                let response = await app.$http.get('/api/v1/mahkeme/');
                commit('setMahkemeler', response.data);
            }
            return state.mahkemeler;
        },
        async getNoterler({state, commit}) {
            if (state.noterler.length == 0) {
                let response = await app.$http.get('/api/v1/noter/');
                commit('setNoterler', response.data);
            }
            return state.noterler;
        },
        async getPersoneller({state, commit}) {
            if (state.personeller.length == 0) {
                let response = await app.$http.get("/api/v1/personel");
                commit('setPersoneller', response.data)
            }
            return state.personeller;
        },
        async loginWithSso({state, commit}) {
            Vue.prototype.$http.get('/api/v1/oauth/login').then((res: any) => {
                location.href = res.data;
            })
        },
        async tenantData({state,dispatch}){
            return new Promise(async (resolve, reject)=>{
                await dispatch('getTenantList');
                resolve(state.tenantList.find(item => item.tenant_id == state.tenant_id));
            });
        }
    },
    mutations: {
        setIcraProTakipBilgileri(state, data) {
            state.icraProTakipBilgileri = data;
        },
        setRouter(state: AppState, router: VueRouter) {
            state.router = router;
        },
        setUser(state: AppState, user: PersonelEntity) {
            state.user = user;
            sessionStorage.setItem("user", JSON.stringify(user));
            // Sunucudan gelen taze `/ben` yanıtı kapı alanını taşıyorsa kapı değeri TAZEDİR:
            // `senkronDurumunuTazele` yarım saat boyunca ikinci bir `/ben` atmaz.
            if (user && typeof (user as any).senkron_aktif === "boolean") senkronKapiZamaniYaz();
        },
        setTenantId(state: AppState, tenantId: string) {
            sessionStorage.setItem("tenantId", tenantId);
            let tenant = state.tenantList.find(tenant=>tenant.tenant_id == tenantId);
            if(tenant)
                sessionStorage.setItem("selectedTenant", JSON.stringify(tenant));
            state.tenant_id = tenantId;
        },
        /**
         * Otomatik sorgu kapısını günceller (`/ben` tazelemesi ya da sunucunun 409
         * `senkron_kapali` cevabı). Kullanıcının geri kalanına dokunmaz; giriş yapılmamışken
         * (kullanıcı yok) hiçbir şey yazmaz.
         */
        setSenkronAktif(state: AppState, aktif: boolean) {
            if (!state.user || !state.user.id) return;
            state.user = {...state.user, senkron_aktif: aktif};
            sessionStorage.setItem("user", JSON.stringify(state.user));
        },
        setAccessToken(state: AppState, accessToken: string) {
            state.accessToken = accessToken;
            localStorage.setItem("accessToken", accessToken);
        },
        setActiveProcess(state: AppState, activeProcess: any) {
            if (activeProcess.isRoot) {
                state.activeProcesses = [];
            }
            state.activeProcesses.push(activeProcess);
            sessionStorage.setItem("activeProcesses", JSON.stringify(state.activeProcesses))
        },
        setUyapConnection(state: AppState, payload: boolean) {
            state.uyapConnection = payload;
        },
        setUyapAvukatBilgileri(state: AppState, avukat: KullaniciBilgileriCevap) {
            state.uyapAvukatBilgileri = avukat;
        },
        setUyapAvukatBakiye(state: AppState, bakiye: string) {
            state.uyapAvukatBakiye = bakiye;
        },
        setUyapSorguBakiye(state: AppState, bakiye: string) {
            state.uyapSorguBakiye = bakiye;
        },
        setAvukatlar(state: AppState, data) {
            state.avukatlar = data;
        },
        setMahkemeler(state: AppState, data) {
            const sorted = Array.isArray(data) ? sortMahkemelerByIsimAsc(data) : [];
            state.mahkemeler.splice(0, state.mahkemeler.length, ...sorted);
        },
        resortMahkemeler(state: AppState) {
            const sorted = sortMahkemelerByIsimAsc(state.mahkemeler);
            state.mahkemeler.splice(0, state.mahkemeler.length, ...sorted);
        },
        setNoterler(state: AppState, data) {
            state.noterler = data;
        },
        setTenantList(state: AppState, data) {
            state.tenantList = data;
        },
        setKurlar(state: AppState, data) {
            state.kurlar = data;
        },
        setTenantListPromise(state: AppState, promise) {
            state.tenantListPromise = promise;
        },
        setSenkronTazelemeIstegi(state: AppState, istek: any) {
            state.senkronTazelemeIstegi = istek;
        },
        setSenkronSonDenemeZamani(state: AppState, zaman: number) {
            state.senkronSonDenemeZamani = zaman;
        },
        setPersoneller(state: AppState, data) {
            state.personeller = data;
        },
    },
    getters: {
        router: (state: AppState) => state.router,
        accessToken: (state: AppState) => state.accessToken,
        isDev: (state: AppState) => !window.location.hostname.includes('.com'),
        isEicraPro: (state: AppState) => window.location.pathname.startsWith(ProgramPaths.eicrapro),
        isIcraPro: (state: AppState) => window.location.pathname.startsWith(ProgramPaths.icrapro),
        isOfisPro: (state: AppState) => window.location.pathname.startsWith(ProgramPaths.ofispro),
        isDavaPro: (state: AppState) => window.location.pathname.startsWith(ProgramPaths.davapro),
        isArabulucuPro: (state: AppState) => window.location.pathname.startsWith(ProgramPaths.arabulucupro),
        // isImzaConnected artık imzaKimligi modülünde (socket bağlı + son kimlik devri ok).
        // Geliştirici modunda (mock veri) UYAP bağlı görünür; mod kapanınca gerçek socket değeri geri gelir.
        isUyapConnected: (state: AppState) => state.eicraproDevMode === true || state.uyapConnection,
        home: (state: AppState) => ProgramPaths.ofispro,
        isEicraproDevMode: (state: AppState) => state.eicraproDevMode,
        avukatBilgileri: (state: AppState) => state.uyapAvukatBilgileri,
        avukatBakiye: (state: AppState) => state.uyapAvukatBakiye,
        sorguBakiye: (state: AppState) => state.uyapSorguBakiye,
        ssoConfig: (state: AppState) => state.ssoConfig,
        userData: (state: AppState) => state.user,
        // Otomatik (zamanlanmış) sorgular açık mı? Karar 20.10.2026: UYAP yoğun istek
        // kısıtlaması yüzünden canlıya KAPALI çıkar; sunucudaki tek ayar
        // (SORGU_SENKRON_AKTIF) `/ben` yanıtında üst düzey `senkron_aktif` olarak gelir.
        // FAIL-CLOSED: alan yoksa (eski backend, bayat oturum) ya da `true` değilse KAPALI.
        // Menü kalemi, plan ekranı rotası, İşlemler şeridi ve 22:45/08:00 zamanlayıcıları
        // yalnız bu getter'ı okur (bkz. utils/senkronKapisi.ts).
        senkronAktif: (state: AppState) => (state.user as any)?.senkron_aktif === true,
        // Ürün-bazlı yenileme teklifi. Parametreli getter: Vuex memoize etmez,
        // bileşen anahtarı reaktif $route.path'ten türetip verir (window.location
        // Vuex getter'ında reaktif olmadığından ürün geçişinde bayat kalıyordu).
        //
        // KAYNAK: /api/v1/ben yanıtındaki `lisans_durum` özeti. Ayrı bir
        // /renewal-offer isteği ATILMAZ — özet zaten kullanıcıyla birlikte
        // sessionStorage'da durduğu için sayfa yenilemesi sıfır istek eder.
        // Kademe/gün hesabı burada yapılır ki gece yarısı bayatlamasın.
        renewalOfferFor: (state: AppState) => (key: 'icrapro' | 'eicrapro'): any => {
            const durum: any = (state.user as any)?.lisans_durum;
            if (!durum) return null;
            return offerFromLisansDurum(durum[key]);
        },
        // Ürün-bazlı demo durumu. renewalOfferFor ile aynı desen ve aynı kaynak.
        demoStatusFor: (state: AppState) => (key: 'icrapro' | 'eicrapro'): any => {
            const durum: any = (state.user as any)?.lisans_durum;
            if (!durum) return null;
            return demoStatusFromLisansDurum(durum[key]);
        },
        licenses: (state: AppState) => state.user.licenses.filter(licence => licence.tenant_id == state.tenant_id),
        // DEV-2113 — /ben kural özeti; yoksa null (ekran kısıtı göstermez, sunucu engeller).
        ozellikDurumu: (state: AppState) => state.user?.ozellik_durumu ?? null,
        tenant: (state: AppState) => state.tenantList.find(item => item.tenant_id == state.tenant_id)
    }
});
